/**
 * Нормализация текста для поиска по залам.
 *
 * Три проблемы, которые она решает:
 *
 * 1. Регистр. В SQLite LIKE регистронезависим только для латиницы, поэтому
 *    «Зенит» не нашёлся бы по «зенит». Всё приводим к нижнему регистру сами.
 *
 * 2. Раскладка названий. Сети пишутся латиницей — World Class, Alex Fitness,
 *    DDX, — а ищут их кириллицей: «ворлд класс». Поэтому к строке поиска
 *    дописывается транслитерация в обе стороны.
 *
 * 3. Удвоенные буквы. Транслитерация «fitness» даёт «фитнесс», а набирают
 *    «фитнес»; «класс» против «class» — та же история с другой стороны.
 *    Схлопываем удвоения и в поле, и в запросе — тогда обе формы совпадают.
 *
 * Одна функция на импортёр и на API: если они разойдутся, поиск начнёт молча
 * не находить половину залов.
 */
/** Латиница → кириллица. Диграфы идут первыми: порядок здесь значим. */
const TO_CYRILLIC = [
    ['shch', 'щ'],
    ['sch', 'щ'],
    ['yo', 'ё'],
    ['zh', 'ж'],
    ['kh', 'х'],
    ['ts', 'ц'],
    ['ch', 'ч'],
    ['sh', 'ш'],
    ['yu', 'ю'],
    ['ya', 'я'],
    ['ck', 'к'],
    ['ph', 'ф'],
    ['qu', 'кв'],
    ['ee', 'и'],
    ['oo', 'у'],
    ['a', 'а'],
    ['b', 'б'],
    ['c', 'к'],
    ['d', 'д'],
    ['e', 'е'],
    ['f', 'ф'],
    ['g', 'г'],
    ['h', 'х'],
    ['i', 'и'],
    ['j', 'дж'],
    ['k', 'к'],
    ['l', 'л'],
    ['m', 'м'],
    ['n', 'н'],
    ['o', 'о'],
    ['p', 'п'],
    ['q', 'к'],
    ['r', 'р'],
    ['s', 'с'],
    ['t', 'т'],
    ['u', 'у'],
    ['v', 'в'],
    ['w', 'в'],
    ['x', 'кс'],
    ['y', 'й'],
    ['z', 'з'],
];
/** Кириллица → латиница: для тех, кто набирает «sportlife» в английской раскладке. */
const TO_LATIN = {
    а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh',
    з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o',
    п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'ts',
    ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu',
    я: 'ya',
};
/**
 * Побуквенная транслитерация не всегда даёт то, что набирают: «house»
 * превращается в «хоусе», а ищут «хаус». Здесь — слова, которые реально
 * встречаются в названиях залов справочника (по частоте в базе) и у которых
 * побуквенный вариант расходится с произношением.
 *
 * У слипшихся названий в замене стоит и раздельная форма («xfit» → «иксфит
 * икс фит»): набирают и так, и так.
 */
const WORD_ALIASES = {
    athletic: 'атлетик',
    body: 'боди',
    bright: 'брайт',
    cycle: 'сайкл',
    dance: 'дэнс',
    ddx: 'ддх',
    drive: 'драйв',
    ems: 'эмс',
    energy: 'энерджи',
    face: 'фейс',
    fitcurves: 'фиткервс фит кервс',
    gym: 'джим',
    hall: 'холл',
    house: 'хаус',
    iron: 'айрон',
    jump: 'джамп',
    justfit: 'джастфит джаст фит',
    lady: 'леди',
    life: 'лайф',
    myfitlab: 'майфитлаб май фит лаб',
    power: 'пауэр',
    powerhouse: 'пауэрхаус пауэр хаус',
    pride: 'прайд',
    sky: 'скай',
    space: 'спейс',
    sportlife: 'спортлайф спорт лайф',
    stretch: 'стретч',
    stretching: 'стретчинг',
    style: 'стайл',
    the: 'зе',
    time: 'тайм',
    wegym: 'виджим ви джим',
    woman: 'вумен',
    xfit: 'иксфит икс фит',
    yobody: 'йободи йо боди',
};
const HAS_LATIN = /[a-z]/;
const HAS_CYRILLIC = /[а-яё]/;
export function toCyrillic(text) {
    let out = '';
    let i = 0;
    outer: while (i < text.length) {
        for (const [from, to] of TO_CYRILLIC) {
            if (text.startsWith(from, i)) {
                out += to;
                i += from.length;
                continue outer;
            }
        }
        out += text[i];
        i += 1;
    }
    return out;
}
export function toLatin(text) {
    let out = '';
    for (const char of text)
        out += TO_LATIN[char] ?? char;
    return out;
}
/** «класс» и «clas» должны сойтись — схлопываем подряд идущие одинаковые буквы. */
function collapseDoubles(text) {
    return text.replace(/(.)\1+/g, '$1');
}
/** Приводит к нижнему регистру и убирает всё, кроме букв, цифр и пробелов. */
function clean(text) {
    return text
        .toLowerCase()
        .replace(/ё/g, 'е')
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim();
}
/**
 * Строка, по которой ищем: сам текст плюс его транслитерация.
 * Кладётся в Gym.searchText при импорте.
 */
export function searchIndexOf(parts) {
    const base = clean(parts.filter(Boolean).join(' '));
    if (!base)
        return '';
    const variants = [base];
    if (HAS_LATIN.test(base)) {
        variants.push(toCyrillic(base));
        const spoken = base
            .split(' ')
            .map((word) => WORD_ALIASES[word] ?? word)
            .join(' ');
        if (spoken !== base)
            variants.push(toCyrillic(spoken));
    }
    if (HAS_CYRILLIC.test(base))
        variants.push(toLatin(base));
    const unique = [...new Set(variants.map(collapseDoubles))];
    return unique.join(' ');
}
/**
 * Слова запроса. Каждое ищется отдельно и по И — чтобы «world class москва»
 * находило клуб, у которого сеть и город стоят в строке не подряд.
 */
export function searchTermsOf(query) {
    const base = clean(query);
    if (!base)
        return [];
    return base
        .split(' ')
        .filter((word) => word.length > 0)
        .map(collapseDoubles)
        .slice(0, 6);
}
//# sourceMappingURL=search.js.map